import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Receipt, Navigation, Truck, CalendarDays, ChevronDown } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getEarningsStats } from '../api/admin';
import { getVehicleTypes } from '../api/vehicleTypes';
import StatCard from '../components/ui/StatCard';
import Card from '../components/ui/Card';
import DatePicker from '../components/ui/DatePicker';
import { Table } from '../components/ui/Table';
import { StatCardSkeleton, Skeleton } from '../components/ui/Skeleton';
import { formatCurrency } from '../lib/utils';

const GROUP_OPTIONS = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

const RANGE_LABEL = { day: 'Last 30 days', week: 'Last 12 weeks', month: 'Last 12 months' };
const TODAY_ISO = new Date().toISOString().slice(0, 10);

function formatPeriodLabel(period, groupBy) {
  if (groupBy === 'month') {
    return new Date(`${period}-01`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  }
  if (groupBy === 'week') {
    return `Wk of ${new Date(period).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}`;
  }
  return new Date(period).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function formatDayLabel(isoDate) {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function Earnings() {
  const [groupBy, setGroupBy] = useState('day');
  const [vehicleType, setVehicleType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const { data: vehicleTypesData } = useQuery({
    queryKey: ['vehicle-types'],
    queryFn: () => getVehicleTypes().then((r) => r.data),
  });
  const vehicleTypes = Array.isArray(vehicleTypesData) ? vehicleTypesData : [];

  const { data: stats, isLoading } = useQuery({
    queryKey: ['earnings-stats', groupBy, vehicleType],
    queryFn: () => getEarningsStats({ groupBy, vehicleType: vehicleType || undefined }).then((r) => r.data),
  });

  // A specific day is answered by its own exact-range query rather than by
  // reading off the day/week/month series, so it's always precise no matter
  // which grouping is currently selected or how far back the date is.
  const { data: dayStats, isLoading: dayLoading } = useQuery({
    queryKey: ['earnings-stats-day', selectedDate, vehicleType],
    queryFn: () => getEarningsStats({
      groupBy: 'day', startDate: selectedDate, endDate: selectedDate, vehicleType: vehicleType || undefined,
    }).then((r) => r.data),
    enabled: Boolean(selectedDate),
  });

  const chartData = useMemo(
    () =>
      (stats?.series ?? []).map((s) => ({
        period: formatPeriodLabel(s.period, groupBy),
        access_fee: s.access_fee,
        service_fee: s.service_fee,
      })),
    [stats, groupBy]
  );

  const byVehicleType = stats?.by_vehicle_type ?? [];

  const statCards = [
    { label: 'Access Fees', value: formatCurrency(stats?.total_access_fee), icon: KeyRound, color: 'indigo' },
    { label: 'Service Fees', value: formatCurrency(stats?.total_service_fee), icon: Receipt, color: 'purple' },
    { label: 'Completed Rides', value: stats?.total_rides, icon: Navigation, color: 'green' },
  ];

  const vehicleColumns = [
    {
      key: 'vehicle_type', label: 'Vehicle Type',
      render: (v) => (
        <span className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
          <Truck size={14} className="text-gray-400" /> {v}
        </span>
      ),
    },
    { key: 'rides', label: 'Completed Rides' },
    { key: 'access_fee', label: 'Access Fees', render: (v) => <span className="font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(v)}</span> },
    { key: 'service_fee', label: 'Service Fees', render: (v) => <span className="font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(v)}</span> },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Earnings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Access fees (on accept) & service fees (on completion) collected from driver wallets</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl shrink-0">
            {GROUP_OPTIONS.map((o) => (
              <button
                key={o.key}
                onClick={() => setGroupBy(o.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  groupBy === o.key
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <select
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              className="appearance-none rounded-xl border-0 bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 pl-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Vehicle Types</option>
              {vehicleTypes.map((vt) => (
                <option key={vt.id} value={vt.name} className="capitalize">{vt.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="w-40 shrink-0">
            <DatePicker
              size="sm"
              value={selectedDate}
              onChange={setSelectedDate}
              max={TODAY_ISO}
              placeholder="Pick a day"
            />
          </div>
        </div>
      </div>

      {selectedDate && (
        <Card className="flex items-center justify-between flex-wrap gap-4 border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/30">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-xl p-2.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 shrink-0">
              <CalendarDays size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Earnings on {vehicleType ? <span className="capitalize">{vehicleType}</span> : 'all vehicles'}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{formatDayLabel(selectedDate)}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {dayLoading ? (
              <Skeleton className="h-7 w-40" />
            ) : (
              <>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Access Fees</p>
                  <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">{formatCurrency(dayStats?.total_access_fee ?? 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Service Fees</p>
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400 whitespace-nowrap">{formatCurrency(dayStats?.total_service_fee ?? 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Rides</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">{dayStats?.total_rides ?? 0}</p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => <StatCardSkeleton key={i} />)
          : statCards.map((card) => <StatCard key={card.label} {...card} />)}
      </div>

      <Card padding={false}>
        <div className="px-6 pt-5 pb-2 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Access & Service Fees Over Time</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {RANGE_LABEL[groupBy]}{vehicleType && <> · <span className="capitalize">{vehicleType}</span></>}
          </p>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="h-[260px] flex items-center justify-center">
              <Skeleton className="h-full w-full rounded-xl" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="accessFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="serviceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.15)" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v) => formatCurrency(v)}
                  contentStyle={{ background: 'var(--tw-bg, #fff)', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: 12 }}
                />
                <Area type="monotone" dataKey="access_fee" name="Access Fees" stroke="#4f46e5" strokeWidth={2.5} fill="url(#accessFill)" dot={false} activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="service_fee" name="Service Fees" stroke="#a855f7" strokeWidth={2.5} fill="url(#serviceFill)" dot={false} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No data</div>
          )}
        </div>
      </Card>

      {!vehicleType && (
        <Card padding={false}>
          <div className="px-6 pt-5 pb-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">By Vehicle Type</h3>
            <p className="text-xs text-gray-400 mt-0.5">{RANGE_LABEL[groupBy]}</p>
          </div>
          <Table columns={vehicleColumns} data={byVehicleType} loading={isLoading} emptyMessage="No completed rides in this period" />
        </Card>
      )}
    </div>
  );
}
